import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useSession } from '@/client/lib/auth'
import { CheckCircle, Loader2, Send } from 'lucide-react'

/**
 * Zod schema for issue report form
 */
const issueReportSchema = z.object({
  type: z.enum(['bug', 'feature', 'question', 'other']),
  subject: z.string().min(5, 'Subject must be at least 5 characters').max(100),
  description: z.string().min(20, 'Please provide more detail (at least 20 characters)').max(2000),
})

type IssueReportInput = z.infer<typeof issueReportSchema>

/**
 * IssueReportForm Component
 *
 * Allows users to report issues, suggest features, or ask questions.
 * Submits as a special enquiry type that admins can view.
 */
export function IssueReportForm() {
  const { data: session } = useSession()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const form = useForm<IssueReportInput>({
    resolver: zodResolver(issueReportSchema as any),
    defaultValues: {
      type: 'bug',
      subject: '',
      description: '',
    },
  })

  const onSubmit = async (data: IssueReportInput) => {
    setIsSubmitting(true)
    setError(null)

    try {
      // Submit as an enquiry with formType='feedback'
      const response = await fetch('/api/enquiries/public', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formType: 'feedback',
          name: session?.user?.name || 'Anonymous User',
          email: session?.user?.email || 'no-reply@example.com',
          enquiry: `[${data.type.toUpperCase()}] ${data.subject}\n\n${data.description}`,
          // Store additional context in custom fields
          customFields: JSON.stringify({
            feedbackType: data.type,
            subject: data.subject,
            userId: session?.user?.id,
            userAgent: navigator.userAgent,
            url: window.location.href,
          }),
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to submit feedback')
      }

      setIsSubmitted(true)
      form.reset()
    } catch (err) {
      console.error('Error submitting feedback:', err)
      setError('Failed to submit your feedback. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
        <h3 className="text-lg font-semibold mb-2">Thank You!</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Your feedback has been submitted. We'll review it shortly.
        </p>
        <Button variant="outline" onClick={() => setIsSubmitted(false)}>
          Submit Another
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="type">Type</Label>
        <Select
          value={form.watch('type')}
          onValueChange={(value: 'bug' | 'feature' | 'question' | 'other') =>
            form.setValue('type', value)
          }
        >
          <SelectTrigger id="type">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bug">Bug Report</SelectItem>
            <SelectItem value="feature">Feature Request</SelectItem>
            <SelectItem value="question">Question</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="subject">Subject</Label>
        <Input
          id="subject"
          {...form.register('subject')}
          placeholder="Brief summary of your feedback"
        />
        {form.formState.errors.subject && (
          <p className="text-sm text-destructive">
            {form.formState.errors.subject.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          {...form.register('description')}
          placeholder="Please describe the issue or suggestion in detail..."
          rows={5}
        />
        {form.formState.errors.description && (
          <p className="text-sm text-destructive">
            {form.formState.errors.description.message}
          </p>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="h-4 w-4 mr-2" />
            Submit Feedback
          </>
        )}
      </Button>
    </form>
  )
}
